import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { api } from "@/lib/apiClient";
import { resetPageMeta } from "@/lib/seo";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { QrCode, Check, Zap, CalendarClock, BarChart3, Repeat, Star, MessageCircle, Link2, ShieldCheck, X, Clock, Users } from "lucide-react";

const DEMO_QR = `https://wa.me/?text=${encodeURIComponent("Hi! I scanned a DynaQR demo — one QR, every occasion.")}`;

const DEMO = [
  { key: "expo", label: "Trade Expo", type: "url", target: "https://dynaqr.in/page/dynamic-qr-for-events", text: "→ Opens your website / event page" },
  { key: "meetup", label: "Networking Meet", type: "whatsapp", target: `https://wa.me/?text=${encodeURIComponent("Hi! Great meeting you today. Let's stay connected 🙌")}`, text: "Hi! Great meeting you today. Let's stay connected 🙌" },
  { key: "promo", label: "Festive Promo", type: "whatsapp", target: `https://wa.me/?text=${encodeURIComponent("Here's your 20% festive offer — reply to claim!")}`, text: "Here's your 20% festive offer — reply to claim!" },
];

const TESTIMONIALS = [
  { name: "Rahul Mehta", role: "Founder, PrintWorks", quote: "I print one QR on my visiting card and switch the message for every event. Game changer — no more reprinting." },
  { name: "Sneha Iyer", role: "Real Estate Consultant", quote: "At expos it opens my listings page; at meetups it opens WhatsApp. Same QR. Clients love how smooth it is." },
  { name: "Arjun Verma", role: "Small Business Owner", quote: "Scheduling switches means my QR is always relevant. The scan analytics show me exactly what's working." },
];

const FAQS = [
  { q: "Do I need to reprint my QR when I change the content?", a: "Never. The QR code stays exactly the same. You change what it points to from your dashboard anytime." },
  { q: "What can the QR open?", a: "Each event is either a pre-filled WhatsApp message (to your number) or any link/website URL. Switch between them anytime." },
  { q: "How does payment work?", a: "It's ₹499/year via Google Pay / UPI. Submit your payment reference and your account is typically activated within a few hours after a quick verification." },
  { q: "Can I schedule switches automatically?", a: "Yes. Set a start (and optional end) time and the QR switches content on its own — perfect for multi-session event days." },
  { q: "What's your refund policy?", a: "If DynaQR isn't working for you, contact support within 7 days of your first payment for a full refund. See our Refund Policy for details." },
];

function HeroDemo() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % DEMO.length), 2800);
    return () => clearInterval(t);
  }, []);
  const current = DEMO[idx];
  return (
    <div className="mx-auto max-w-lg" data-testid="hero-demo">
      <div className="rounded-3xl border border-border bg-card p-5 shadow-xl">
        <div className="grid grid-cols-[auto_1fr] gap-5 items-center">
          <div className="relative">
            <div className="rounded-2xl bg-white p-3 shadow-lg glow-green">
              <QRCodeCanvas value={DEMO_QR} size={132} level="H" />
            </div>
            <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-foreground px-2.5 py-1 text-[10px] font-semibold text-background">Same QR — never changes</span>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
              <span className="relative flex h-2 w-2"><span className="live-dot absolute inline-flex h-full w-full rounded-full bg-accent" /></span>
              Currently sending
            </div>
            <div key={idx} className="hero-demo-fade rounded-xl border border-border bg-secondary/50 p-3.5">
              <div className="flex items-center gap-2 text-accent mb-1.5">
                {current.type === "url" ? <Link2 className="h-4 w-4 shrink-0" /> : <MessageCircle className="h-4 w-4 shrink-0" />}
                <span className="font-display font-bold text-base tracking-tight text-foreground truncate">{current.label}</span>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">{current.text}</p>
            </div>
            <div className="mt-3 flex gap-1.5">
              {DEMO.map((d, i) => (
                <button key={d.key} onClick={() => setIdx(i)} aria-label={`Show ${d.label}`}
                  className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? "w-7 bg-accent" : "w-1.5 bg-border hover:bg-muted-foreground"}`} />
              ))}
            </div>
          </div>
        </div>
      </div>
      <p className="mt-3 text-center text-xs text-muted-foreground">One printed QR — the content switches automatically. ✨</p>
    </div>
  );
}


function StatBar() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.publicStats().then(setStats).catch(() => {}); }, []);
  const nice = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`);
  const showUsers = stats && stats.users >= 10;
  const showScans = stats && stats.scans >= 50;
  const items = [];
  if (showUsers) items.push({ icon: Users, value: `${nice(stats.users)}+`, label: "businesses onboarded" });
  if (showScans) items.push({ icon: BarChart3, value: `${nice(stats.scans)}+`, label: "scans routed" });
  if (items.length === 0) {
    items.push(
      { icon: Repeat, value: "1 QR", label: "unlimited events" },
      { icon: ShieldCheck, value: "7-day", label: "money-back guarantee" },
      { icon: Clock, value: "Hours", label: "to activation" },
    );
  } else if (items.length === 1) {
    items.push({ icon: ShieldCheck, value: "7-day", label: "money-back guarantee" });
  }
  return (
    <section className="mx-auto max-w-3xl px-4 sm:px-6 pb-16" data-testid="landing-stats">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {items.map((it) => (
          <div key={it.label} className="rounded-2xl border border-border bg-card px-4 py-5 text-center">
            <it.icon className="h-5 w-5 text-accent mx-auto mb-2" />
            <div className="font-display text-2xl font-extrabold tracking-tighter">{it.value}</div>
            <div className="text-xs text-muted-foreground mt-1">{it.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

const beforeItems = [
  "Reprint cards every time your message changes",
  "One QR = one fixed link, forever",
  "No idea who scanned or when",
  "Wasted print costs for every event",
];
const afterItems = [
  "Print once — change the content anytime",
  "One QR works for meetups, expos & promos",
  "See every scan by event and day",
  "Zero reprinting, zero waste",
];

function BeforeAfter() {
  return (
    <section className="mx-auto max-w-5xl px-4 sm:px-6 pb-16" data-testid="landing-before-after">
      <h2 className="font-display text-3xl font-extrabold tracking-tighter text-center mb-8">A static QR holds you back. DynaQR doesn't.</h2>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="rounded-3xl border border-border bg-secondary/30 p-7">
          <p className="font-display text-sm font-bold uppercase tracking-widest text-muted-foreground mb-5">The old way</p>
          <ul className="space-y-3.5">
            {beforeItems.map((t) => (
              <li key={t} className="flex items-start gap-3 text-sm text-muted-foreground">
                <span className="grid h-5 w-5 place-items-center rounded-full bg-destructive/10 text-destructive shrink-0 mt-0.5"><X className="h-3 w-3" strokeWidth={3} /></span>
                {t}
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-3xl border-2 border-accent bg-card p-7 glow-green">
          <p className="font-display text-sm font-bold uppercase tracking-widest text-accent mb-5">With DynaQR</p>
          <ul className="space-y-3.5">
            {afterItems.map((t) => (
              <li key={t} className="flex items-start gap-3 text-sm">
                <span className="grid h-5 w-5 place-items-center rounded-full bg-accent/15 text-accent shrink-0 mt-0.5"><Check className="h-3 w-3" strokeWidth={3} /></span>
                {t}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function ProductPreview() {
  return (
    <section className="mx-auto max-w-5xl px-4 sm:px-6 pb-16" data-testid="landing-product-preview">
      <h2 className="font-display text-3xl font-extrabold tracking-tighter text-center mb-2">Your dashboard, one tap away</h2>
      <p className="text-center text-sm text-muted-foreground mb-8">Switch what your QR opens, schedule changes and track scans — all from a simple dashboard.</p>
      <div className="rounded-3xl border border-border bg-card p-4 sm:p-6 shadow-lg">
        <div className="grid md:grid-cols-[1fr_1.4fr] gap-5">
          <div className="rounded-2xl border border-border bg-secondary/30 p-5 flex flex-col items-center justify-center text-center">
            <div className="rounded-xl bg-white p-3 shadow"><QRCodeCanvas value={DEMO_QR} size={128} bgColor="#ffffff" fgColor="#0A0A0A" level="M" /></div>
            <p className="mt-3 font-display font-bold text-sm tracking-tight">Your permanent QR</p>
            <p className="text-xs text-muted-foreground">Download · print · never changes</p>
          </div>
          <div className="rounded-2xl border border-border p-5">
            <div className="flex items-center justify-between mb-4">
              <p className="font-display font-bold text-sm tracking-tight">Your events</p>
              <span className="text-xs rounded-full bg-accent/15 text-accent px-2.5 py-1 font-medium">Live: Networking Meet</span>
            </div>
            <div className="space-y-2.5">
              {[
                { icon: MessageCircle, name: "Networking Meet", meta: "WhatsApp message", live: true },
                { icon: Link2, name: "Trade Expo", meta: "Opens your website", live: false },
                { icon: MessageCircle, name: "Festive Promo", meta: "WhatsApp offer", live: false },
              ].map((e) => (
                <div key={e.name} className={`flex items-center gap-3 rounded-xl border p-3 ${e.live ? "border-accent bg-accent/5" : "border-border"}`}>
                  <span className="grid h-8 w-8 place-items-center rounded-lg bg-secondary text-foreground shrink-0"><e.icon className="h-4 w-4" /></span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{e.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{e.meta}</p>
                  </div>
                  <span className="ml-auto text-xs font-medium">
                    {e.live ? <span className="inline-flex items-center gap-1.5 text-accent"><span className="relative flex h-2 w-2"><span className="live-dot absolute inline-flex h-full w-full rounded-full bg-accent" /></span>Live</span> : <span className="text-muted-foreground">Set live</span>}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


const features = [
  "1 Dynamic QR", "Unlimited Events", "Switch your message anytime", "Schedule automatic switching",
  "Scan analytics", "Event-wise analytics", "QR download", "CSV export", "No platform branding",
];

function Pricing() {
  const [plans, setPlans] = useState([]);
  useEffect(() => { api.getPlans().then((p) => Array.isArray(p) && setPlans(p)).catch(() => {}); }, []);
  const fallback = [
    { id: "solo", name: "Solo", price: 499, qr_limit: 1, tagline: "For individual networkers", features: features.slice(0, 6) },
    { id: "business", name: "Business", price: 1499, qr_limit: 5, tagline: "For growing small businesses", features: ["5 Dynamic QRs", "Name each QR", "Unlimited events per QR", "Scheduled switching", "Per-QR analytics + CSV", "Priority email support"] },
    { id: "pro", name: "Pro", price: 1999, qr_limit: 10, tagline: "For agencies & larger teams", features: ["10 Dynamic QRs", "Name each QR", "Unlimited events per QR", "Scheduled switching", "Per-QR analytics + CSV", "Priority email support"] },
  ];
  const list = plans.length ? plans : fallback;
  return (
    <div className="grid md:grid-cols-3 gap-5 items-start">
      {list.map((p) => {
        const featured = p.id === "business";
        return (
          <div key={p.id} data-testid={`pricing-${p.id}`}
            className={`relative rounded-3xl border-2 bg-card p-7 ${featured ? "border-accent glow-green md:-mt-3 md:mb-3" : "border-border"}`}>
            {featured && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-accent px-3 py-1 text-xs font-bold text-accent-foreground">Most popular</span>}
            <p className="font-display text-sm font-bold uppercase tracking-widest text-accent">{p.name}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{p.tagline}</p>
            <div className="mt-3 font-display text-4xl font-extrabold tracking-tighter">₹{p.price}<span className="text-base text-muted-foreground font-medium"> / yr</span></div>
            <p className="mt-1 text-sm font-semibold text-foreground">{p.qr_limit} Dynamic QR{p.qr_limit > 1 ? "s" : ""}</p>
            <ul className="mt-5 space-y-2.5 text-left">
              {(p.features || []).map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm">
                  <span className="grid h-5 w-5 place-items-center rounded-full bg-accent/15 text-accent shrink-0 mt-0.5"><Check className="h-3 w-3" strokeWidth={3} /></span>
                  {f}
                </li>
              ))}
            </ul>
            <Link to="/signup"><Button size="lg" variant={featured ? "default" : "outline"} className="mt-6 w-full rounded-full" data-testid={`pricing-cta-${p.id}`}>Get Started</Button></Link>
          </div>
        );
      })}
    </div>
  );
}


const highlights = [
  { icon: Repeat, title: "One QR, any message", desc: "Print once. Change what people see anytime — a different message for every event." },
  { icon: CalendarClock, title: "Schedule switches", desc: "Auto-switch content by date & time. Never touch the printed QR again." },
  { icon: BarChart3, title: "Know your scans", desc: "Track scans by event, day and more. Export to CSV whenever you like." },
];

export default function Landing() {
  const [testimonials, setTestimonials] = useState(TESTIMONIALS);
  useEffect(() => {
    resetPageMeta();
    api.getTestimonials().then((t) => { if (Array.isArray(t) && t.length) setTestimonials(t); }).catch(() => {});
  }, []);
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="grid h-9 w-9 place-items-center rounded-xl bg-accent text-accent-foreground">
              <QrCode className="h-5 w-5" strokeWidth={2} />
            </div>
            <span className="font-display text-xl font-extrabold tracking-tighter">DynaQR</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login"><Button variant="ghost" className="rounded-full" data-testid="nav-login-btn">Login</Button></Link>
            <Link to="/signup"><Button className="rounded-full" data-testid="nav-signup-btn">Get Started</Button></Link>
          </div>
        </div>
      </header>

      <section className="mx-auto max-w-6xl px-4 sm:px-6 pt-20 pb-12 text-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-1.5 text-xs text-muted-foreground mb-6">
          <Zap className="h-3.5 w-3.5 text-accent" /> For networkers, realtors & small businesses
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-none">
          Stop reprinting<br />your QR codes.
        </h1>
        <p className="mt-6 max-w-xl mx-auto text-base text-muted-foreground leading-relaxed">
          Print <span className="font-semibold text-foreground">one QR</span> — then change what it opens anytime: a WhatsApp message for meetups, your website at expos, an offer during festivals. The printed QR <span className="font-semibold text-foreground">never</span> changes.
        </p>
        <div className="mt-8 flex items-center justify-center gap-3">
          <Link to="/signup"><Button size="lg" className="rounded-full px-8" data-testid="hero-cta-btn">Get Started — ₹499/year</Button></Link>
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-muted-foreground" data-testid="hero-trust-row">
          <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-accent" /> 7-day money-back guarantee</span>
          <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4 text-accent" /> Activated within hours</span>
          <span className="inline-flex items-center gap-1.5"><Check className="h-4 w-4 text-accent" /> No reprinting, ever</span>
        </div>
        <div className="mt-12">
          <HeroDemo />
        </div>
      </section>

      <StatBar />

      <section className="mx-auto max-w-6xl px-4 sm:px-6 pb-16 grid md:grid-cols-3 gap-6">
        {highlights.map((h) => (
          <div key={h.title} className="rounded-2xl border border-border bg-card p-6">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-accent/15 text-accent mb-4">
              <h.icon className="h-5 w-5" />
            </div>
            <h3 className="font-display text-lg font-bold tracking-tight">{h.title}</h3>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{h.desc}</p>
          </div>
        ))}
      </section>

      <BeforeAfter />

      <ProductPreview />

      <section className="mx-auto max-w-5xl px-4 sm:px-6 pb-16" data-testid="landing-testimonials">
        <h2 className="font-display text-3xl font-extrabold tracking-tighter text-center mb-8">Loved by busy professionals</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((t) => (
            <div key={t.name} className="rounded-2xl border border-border bg-card p-6">
              <div className="flex gap-0.5 mb-3">
                {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-accent text-accent" />)}
              </div>
              <p className="text-sm leading-relaxed">"{t.quote}"</p>
              <div className="mt-4">
                <p className="font-display font-bold tracking-tight text-sm">{t.name}</p>
                <p className="text-xs text-muted-foreground">{t.role}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section id="pricing" className="mx-auto max-w-5xl px-4 sm:px-6 pb-16" data-testid="landing-pricing">
        <h2 className="font-display text-3xl font-extrabold tracking-tighter text-center mb-2">One price a year. No monthly fees.</h2>
        <p className="text-center text-sm text-muted-foreground mb-8">Start with one QR — scale to a whole business when you're ready.</p>
        <Pricing />
        <p className="mt-6 text-center inline-flex w-full items-center justify-center gap-1.5 text-xs text-muted-foreground"><ShieldCheck className="h-3.5 w-3.5 text-accent" /> 7-day money-back guarantee · Pay via Google Pay / UPI · activated within a few hours.</p>
      </section>

      <section className="mx-auto max-w-2xl px-4 sm:px-6 pb-24" data-testid="landing-faq">
        <h2 className="font-display text-3xl font-extrabold tracking-tighter text-center mb-2">Frequently asked questions</h2>
        <p className="text-center text-sm text-muted-foreground mb-8">Everything you need to know before you start.</p>
        <Accordion type="single" collapsible className="w-full">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-${i}`}>
              <AccordionTrigger className="text-left font-medium">{f.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <div className="mt-6 rounded-xl border border-border bg-secondary/40 p-5 text-sm text-muted-foreground" data-testid="refund-note">
          <span className="font-semibold text-foreground">Refund policy:</span> Not satisfied? Request a full refund within 7 days of your first payment — no questions asked. Read the full <Link to="/policy/refund" className="text-accent hover:underline">Refund &amp; Cancellation Policy</Link>.
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-center text-xs text-muted-foreground">
          <span>© {new Date().getFullYear()} DynaQR · One QR. Every occasion.</span>
          <nav className="flex flex-wrap items-center justify-center gap-4">
            <Link to="/policy/terms" className="hover:text-foreground" data-testid="footer-terms">Terms</Link>
            <Link to="/policy/privacy" className="hover:text-foreground" data-testid="footer-privacy">Privacy</Link>
            <Link to="/policy/refund" className="hover:text-foreground" data-testid="footer-refund">Refund Policy</Link>
            <Link to="/policy/data" className="hover:text-foreground" data-testid="footer-data">Data Policy</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
